import React, {useState, useEffect} from 'react';
import Cards from '../Cards';
import Section from '../UI/Section';
import Button from '../UI/Button';
import {Context} from '../../context';
import LoadingSpinner from '../UI/LoadingSpinner';
import {
  cardPaymentMethodExists,
  getAccountBalanceInDollars
} from '../../helperFunctions';
import {autoReplenish} from '../../settings';
import LoadFundsUI from './LoadFundsUI';
import AutoLoad from './AutoLoad';

const loadOptionsDefault = [5, 10, 20, 50];
const MIN_LOAD_VALUE = 0.1;

function LoadFunds() {
  const {
    account,
    localeText,
    showError,
    loading,
    paymentProcessing,
    existingPaymentMethodAddBalance,
    oneTimePaymentAddBalance,
    oneTimePaymentToken,
    setSnackBarMessage,
    selectedCardID
  } = React.useContext(Context);
  const [loadValue, setLoadValue] = useState(0);
  const [paymentMethodAvailable, setPaymentMethodAvailable] = useState(false);

  useEffect(() => {
    setPaymentMethodAvailable(
      account?.paymentMethods?.filter(
        ({nickname}) => nickname !== 'Transit Account'
      ).length > 0
        ? true
        : false
    );
  }, [account]);

  const addValue = event => {
    const accountBalanceInDollars = getAccountBalanceInDollars(account);

    if (
      accountBalanceInDollars < 0 &&
      loadValue < Math.abs(accountBalanceInDollars)
    ) {
      alert(
        localeText(
          'rw_loadfunds_text_input_lower_than_neg_bal',
          'It appears you have a negative balance. The load amount must be the equal to or more than your negative balance'
        )
      );
      return;
    }

    const card = document
      .querySelector('.selected-payment-card')
      ?.id.replace('card-', ''); // each id is prefixed w/ 'card-', purposed for finding elements for wcag testing

    if (card === undefined) {
      showError('rw_msg_error_no_card_selected');
      return;
    }

    if (loadValue < MIN_LOAD_VALUE) {
      showError('rw_msg_error_invalid_amount', 'Please enter a larger amount');
      return;
    }

    if (oneTimePaymentToken && oneTimePaymentToken?.token === selectedCardID) {
      return oneTimePaymentAddBalance({
        oneTimePaymentToken: oneTimePaymentToken.token,
        transitAccountId: account.transitAccounts[0].id,
        amount: Math.round(loadValue * 100)
      }).then(ret => {
        if (ret && ret.errors) {
          return showError(ret.errors);
        }
        setSnackBarMessage(
          `$${loadValue} ${localeText(
            'rw_loadfunds_text_added_successfully_to_transit_account',
            'added successfully to Transit Account'
          )}`
        );
        setLoadValue(0);
      });
    }

    const paymentMethod = account?.paymentMethods?.find(pm => pm.id === card);
    if (paymentMethod === undefined) {
      showError('rw_msg_error_no_payment_method_selected');
      return;
    }

    if (paymentMethod.processor === 'STORED_VALUE') {
      showError('rw_msg_error_invalid_payment_method');
      return;
    }

    existingPaymentMethodAddBalance({
      paymentMethod,
      transitAccount: account.transitAccounts[0],
      amount: Math.round(loadValue * 100)
    }).then(ret => {
      if (ret && ret.errors) {
        return showError(ret.errors);
      }
      setSnackBarMessage(
        `$${loadValue} ${localeText(
          'rw_loadfunds_text_added_successfully_to_transit_account',
          'added successfully to Transit Account'
        )}`
      );
      setLoadValue(0);
    });
  };

  return (
    <div className='w-full grid gap-x-24'>
      <Section
        sectionId='payment'
        heading={localeText('rw_loadfunds_header_payment', 'Payment')}
        className='row-start-2 col-start-1'
      >
        <Cards />
        <Button
          loading={loading}
          onClick={addValue}
          disabled={!paymentMethodAvailable && !oneTimePaymentToken}
          className='w-full mt-8'
        >
          {localeText(
            'rw_loadfunds_button_complete_purchase',
            'Complete Purchase'
          )}
        </Button>
      </Section>
      <Section
        sectionId='load-funds'
        heading={localeText('rw_loadfunds_header_load_funds', 'Load Funds')}
        className='row-start-1 col-start-1'
      >
        <div className='w-fit'>
          <LoadFundsUI
            title='load funds'
            inputValue={loadValue}
            setInputValue={setLoadValue}
            loadOptions={loadOptionsDefault}
          />

          {paymentProcessing && (
            <div className='flex flex-col items-center mt-16'>
              <LoadingSpinner />
              <p className='mt-10 text-xl'>
                {localeText(
                  'rw_loadfunds_text_processing_payment',
                  'Processing Payment'
                )}
                ...
              </p>
            </div>
          )}
        </div>
      </Section>
      <Section
        sectionId='transit-account'
        heading={localeText(
          'rw_loadfunds_header_transit_account',
          'Transit Account'
        )}
        className='row-start-auto col-start-1 lg:row-start-1 lg:col-start-2'
      >
        <h1>
          {localeText('rw_loadfunds_title_balance', 'Balance')}: $
          {(
            (account?.transitAccounts?.[0].storedValue?.balance || 0) / 100
          ).toFixed(2)}
        </h1>
      </Section>
      {autoReplenish && cardPaymentMethodExists(account) && <AutoLoad />}
    </div>
  );
}

export default LoadFunds;
